import { config } from 'src/config'
import { getAddToCart } from 'src/functions/util/dataLayer'
import { getMetafield } from 'src/functions/util/getMetafield'

import type { BaseCartLine, CartLineInput, Product, ProductVariant } from 'src/data/graphql-generated'
import type { CartDiscountCodesUpdateMutationFn } from 'src/hooks/useCartDiscountCodesUpdateMutation'
import type { CartLinesAddMutationFn } from 'src/hooks/useCartLinesAddMutation'
import type { CartLinesUpdateMutationFn } from 'src/hooks/useCartLinesUpdateMutation'
import type { ProductSubscriptionSellingPlans } from 'src/types/Subscription'

export type AddToCartActionParams = {
  product: Product
  variant: ProductVariant
  quantity: number
  addToCartMutationFn: CartLinesAddMutationFn
  cartId?: string
}

export const addToCartAction = async ({
  product,
  variant,
  quantity,
  addToCartMutationFn,
  cartId,
}: AddToCartActionParams) => {
  const cartLines: CartLineInput[] = [{ merchandiseId: variant.id, quantity }]
  const donationVariantId = getMetafield(variant.metafields, 'donation', 'variant')

  if (donationVariantId) {
    cartLines.push({
      merchandiseId: donationVariantId,
      quantity,
      attributes: [{ key: config.donationAttributeKey, value: `${variant.sku}` }],
    })
  }

  await addToCartMutationFn({ cartLines })

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(getAddToCart(product, variant, quantity, cartId))

  return null
}

export type AddToSubscriptionActionParams = {
  product: Product
  variant: ProductVariant
  quantity: number
  sellingPlanInterval: number
  sellingPlans: ProductSubscriptionSellingPlans['sellingPlans']
  existingLines: BaseCartLine[]
  addToCartMutationFn: CartLinesAddMutationFn
  cartLinesUpdateMutationFn: CartLinesUpdateMutationFn
  cartDiscountCodesUpdateMutationFn: CartDiscountCodesUpdateMutationFn
}

export const addToSubscriptionAction = async ({
  product,
  variant,
  quantity,
  sellingPlanInterval,
  sellingPlans,
  existingLines,
  addToCartMutationFn,
  cartLinesUpdateMutationFn,
  cartDiscountCodesUpdateMutationFn,
}: AddToSubscriptionActionParams) => {
  const { coffeeSellingPlanId } = sellingPlans[sellingPlanInterval]

  const cartLines: CartLineInput[] = [{ merchandiseId: variant.id, quantity, sellingPlanId: coffeeSellingPlanId }]
  const donationVariantId = getMetafield(variant.metafields, 'donation', 'variant')

  const existingDonationLine = existingLines.find(
    (line) =>
      line.merchandise.id === donationVariantId &&
      line.attributes.some(
        (attribute) => attribute.key === config.donationAttributeKey && attribute.value === `${sellingPlanInterval}W`,
      ),
  )

  if (donationVariantId && !existingDonationLine) {
    cartLines.push({
      merchandiseId: donationVariantId,
      quantity,
      attributes: [{ key: config.donationAttributeKey, value: `${sellingPlanInterval}W` }],
    })
  }

  const { cart: updatedCart, userErrors } = await addToCartMutationFn({ cartLines })

  if (updatedCart) {
    const { id: cartId } = updatedCart

    if (existingDonationLine && !userErrors.length) {
      await cartLinesUpdateMutationFn({
        item: existingDonationLine,
        newQuantity: existingDonationLine.quantity + quantity,
        currentSellingPlanInterval: sellingPlanInterval,
      })
    }

    await cartDiscountCodesUpdateMutationFn({
      cartId,
      discountCodes: [config.subscriptionDefaultVoucherCode],
    })
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(getAddToCart(product, variant, quantity, updatedCart && updatedCart.id))

  return null
}
